import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, LogOut, CheckCircle, Megaphone, Loader2 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { api, apiErrorMessage } from '../apiClient';
import { toast } from 'sonner';

export default function OrgProfilePage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser, setOrgMode } = useAuth();
  const prof = user?.organizer_profile;

  const [name, setName] = useState(prof?.organization_name || '');
  const [desc, setDesc] = useState(prof?.business_description || '');
  const [logo, setLogo] = useState(prof?.logo_url || '');
  const [website, setWebsite] = useState(prof?.website || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prof) {
      setName(prof.organization_name || '');
      setDesc(prof.business_description || '');
      setLogo(prof.logo_url || '');
      setWebsite(prof.website || '');
    }
  }, [prof]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/organizer/profile', {
        organization_name: name.trim() || undefined,
        business_description: desc.trim() || undefined,
        logo_url: logo.trim() || null,
        website: website.trim() || null,
      });
      await refreshUser();
      toast.success('Profile saved');
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const switchToCustomer = async () => {
    await setOrgMode(false);
    navigate('/');
  };

  const handleLogout = async () => {
    if (window.confirm('Log out?')) {
      await logout();
      toast.info('Logged out');
      navigate('/welcome');
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-4" data-testid="org-profile-page">
      <h1 className="text-2xl font-black text-gray-900 tracking-tight mb-4">Organizer Profile</h1>

      <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-brand-magenta flex items-center justify-center flex-shrink-0">
          <Megaphone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-[17px] font-black text-gray-900 truncate">{prof?.organization_name || user?.full_name}</h2>
          <p className="text-xs text-gray-500">{user?.email}</p>
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold px-2 py-0.5 rounded-full mt-1">
            <CheckCircle className="w-3 h-3" /> Approved
          </span>
        </div>
      </div>

      <button
        data-testid="switch-to-customer-btn"
        onClick={switchToCustomer}
        className="w-full mt-4 flex items-center justify-center gap-2 bg-pink-50 border border-pink-200 text-brand-magenta p-3 rounded-2xl font-extrabold text-sm hover:bg-pink-100 transition-colors"
      >
        <ArrowLeftRight className="w-4 h-4" /> Switch to Customer View
      </button>

      <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-wider mt-6 mb-3">Organization Details</h3>

      <form onSubmit={save} className="flex flex-col gap-4">
        <Field label="Organization name">
          <input data-testid="org-profile-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vibe Events" className="input-field" />
        </Field>
        <Field label="Business description">
          <textarea data-testid="org-profile-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Tell customers about your organization" rows={4} className="input-field resize-none" />
        </Field>
        <Field label="Logo URL">
          <input data-testid="org-profile-logo-input" value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://..." className="input-field" />
        </Field>
        <Field label="Website">
          <input data-testid="org-profile-website-input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourbrand.com" className="input-field" />
        </Field>

        <button
          data-testid="org-profile-save-btn"
          type="submit"
          disabled={saving}
          className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-70 text-white py-4 rounded-2xl font-black text-[15px] transition-colors mt-2"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Save Changes'}
        </button>
      </form>

      <button
        data-testid="org-logout-btn"
        onClick={handleLogout}
        className="w-full mt-4 flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-500 p-3.5 rounded-2xl font-extrabold text-[15px] transition-colors"
      >
        <LogOut className="w-4 h-4" /> Log Out
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-bold text-gray-500">{label}</label>
      {children}
    </div>
  );
}
